import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useCallback, useEffect } from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { showToast } from '@app/providers/ToastProvider';
import { store } from '@app/redux/store';

import { VerifyPhoneFormProps, VerifyPhoneFormValues } from './interfaces';
import verifyPhoneFormValidationScheme from './validationScheme';

function useVerifyPhoneForm({ initialValues, onSubmit }: Pick<VerifyPhoneFormProps, 'initialValues' | 'onSubmit'>) {
	const { startRequest: sendVerificationPhoneCode, isPending: isSendingVerificationPhoneCode } = useTriggeredRequest(
		AccountClientRequests.Users.sendVerificationPhoneCode,
	);

	const formik = useFormik<VerifyPhoneFormValues>({
		initialValues,
		validationSchema: verifyPhoneFormValidationScheme,
		onSubmit,
	});

	const handleSendVerificationPhoneCode = useCallback(async () => {
		try {
			await sendVerificationPhoneCode({
				branch: store.getState().branchEnvironment.variables.branch_id,
				contact: store.getState().auth.currentUser?.phone || '',
				type: store.getState().auth.verifyPhone.type || 'sms',
			});
		} catch (e) {
			const { response } = e as AxiosError<{
				messages: string[];
			}>;

			showToast.error(response?.data?.messages?.join(' ') || 'Oops. Something went wrong.');
		}
	}, [sendVerificationPhoneCode]);

	useEffect(() => {
		handleSendVerificationPhoneCode();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		isSendingVerificationPhoneCode,
		handleSendVerificationPhoneCode,
		formik,
	};
}

export default useVerifyPhoneForm;
